import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMe } from "../common/useMe";
import { hasClubRole } from "../common/roleAccess";
import {
  fetchRpeAutoCloseSettings,
  patchRpeAutoCloseSettings,
} from "./rpeSessionApi.js";

const MIN_HOURS = 1;
const MAX_HOURS = 168;
const DEFAULT_HOURS = 24;
const PRESET_HOURS = [6, 12, 24, 48, 72];

function clampHours(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_HOURS;
  return Math.min(MAX_HOURS, Math.max(MIN_HOURS, Math.round(parsed)));
}

const RpeAutoCloseSettings = ({ isDarkMode = false }) => {
  const { t } = useTranslation();
  const { me } = useMe();
  const canEdit = hasClubRole(me, "admin");

  const [enabled, setEnabled] = useState(true);
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState(null);
  const lastSavedRef = useRef({ enabled: true, hours: DEFAULT_HOURS });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchRpeAutoCloseSettings();
        if (cancelled) return;
        const nextEnabled = data?.enabled !== false;
        const nextHours = clampHours(data?.hours);
        setEnabled(nextEnabled);
        setHours(nextHours);
        lastSavedRef.current = { enabled: nextEnabled, hours: nextHours };
        setError(null);
      } catch {
        if (!cancelled) setError(t("rpe.auto_close_load_error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const persist = useCallback(async (next) => {
    if (!canEdit) return;
    const payload = {
      enabled: next.enabled !== false,
      hours: clampHours(next.hours),
    };
    if (
      lastSavedRef.current.enabled === payload.enabled &&
      lastSavedRef.current.hours === payload.hours
    ) {
      setHours(payload.hours);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await patchRpeAutoCloseSettings(payload);
      const enabledSaved = saved?.enabled !== false;
      const hoursSaved = clampHours(saved?.hours);
      setEnabled(enabledSaved);
      setHours(hoursSaved);
      lastSavedRef.current = { enabled: enabledSaved, hours: hoursSaved };
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1600);
    } catch (err) {
      setError(t("rpe.auto_close_save_error", { message: err?.message || "" }));
    } finally {
      setSaving(false);
    }
  }, [canEdit, t]);

  const chips = useMemo(() => {
    if (PRESET_HOURS.includes(hours)) return PRESET_HOURS;
    return [...PRESET_HOURS, hours].sort((a, b) => a - b);
  }, [hours]);

  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const controlsDisabled = !canEdit || saving || loading;
  const hoursDisabled = controlsDisabled || !enabled;

  const toggleEnabled = () => {
    if (controlsDisabled) return;
    persist({ enabled: !enabled, hours });
  };

  const selectHours = (value) => {
    if (hoursDisabled) return;
    const nextHours = clampHours(value);
    setHours(nextHours);
    persist({ enabled, hours: nextHours });
  };

  const nudgeHours = (delta) => {
    if (hoursDisabled) return;
    selectHours(hours + delta);
  };

  return (
    <div className={`mt-3 border-t pt-3 ${isDarkMode ? "border-white/10" : "border-amber-200/70"}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-center gap-2">
          <p className={`text-[11px] font-medium uppercase tracking-wide ${muted}`}>
            {t("rpe.auto_close_title")}
          </p>
          {loading ? (
            <Loader2 size={12} className={`animate-spin ${muted}`} />
          ) : (
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label={t("rpe.auto_close_title")}
              disabled={controlsDisabled}
              title={
                enabled
                  ? t("rpe.auto_close_hint_on", { hours })
                  : t("rpe.auto_close_hint_off")
              }
              onClick={toggleEnabled}
              className={`relative h-5 w-9 rounded-full transition ${
                enabled
                  ? isDarkMode
                    ? "bg-amber-400/80"
                    : "bg-amber-500"
                  : isDarkMode
                  ? "bg-gray-600"
                  : "bg-gray-300"
              } ${controlsDisabled ? "cursor-not-allowed opacity-60" : "hover:opacity-90"}`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition ${
                  enabled ? "left-4" : "left-0.5"
                }`}
              />
            </button>
          )}
        </div>

        {!loading && (
          <div
            className={`flex flex-wrap items-center gap-1 ${
              enabled ? "" : "pointer-events-none opacity-40"
            }`}
          >
            <button
              type="button"
              disabled={hoursDisabled || hours <= MIN_HOURS}
              onClick={() => nudgeHours(-1)}
              aria-label={t("rpe.auto_close_decrease")}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition ${
                isDarkMode
                  ? "text-gray-300 hover:bg-white/10"
                  : "text-gray-600 hover:bg-amber-100/80"
              } disabled:cursor-not-allowed disabled:opacity-30`}
            >
              <ChevronLeft size={14} />
            </button>

            <div
              role="group"
              aria-label={t("rpe.auto_close_after")}
              className={`inline-flex flex-wrap items-center gap-0.5 rounded-full p-0.5 ${
                isDarkMode ? "bg-black/25" : "bg-white/70"
              }`}
            >
              {chips.map((value) => {
                const selected = enabled && value === hours;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={hoursDisabled}
                    onClick={() => selectHours(value)}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-medium tabular-nums transition ${
                      selected
                        ? isDarkMode
                          ? "bg-amber-400/20 text-amber-100 ring-1 ring-amber-300/40"
                          : "bg-white text-amber-900 shadow-sm ring-1 ring-amber-200"
                        : isDarkMode
                        ? "text-gray-400 hover:text-gray-200"
                        : "text-gray-500 hover:text-gray-800"
                    } disabled:cursor-not-allowed`}
                  >
                    {t("rpe.auto_close_chip", { hours: value })}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              disabled={hoursDisabled || hours >= MAX_HOURS}
              onClick={() => nudgeHours(1)}
              aria-label={t("rpe.auto_close_increase")}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition ${
                isDarkMode
                  ? "text-gray-300 hover:bg-white/10"
                  : "text-gray-600 hover:bg-amber-100/80"
              } disabled:cursor-not-allowed disabled:opacity-30`}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}

        <div className={`ml-auto flex items-center gap-2 text-[11px] ${muted}`}>
          {savedFlash && canEdit && (
            <span className={`inline-flex items-center gap-1 ${isDarkMode ? "text-emerald-300" : "text-emerald-700"}`}>
              <Check size={11} />
              {t("rpe.auto_close_saved")}
            </span>
          )}
          {!canEdit && (
            <span className="inline-flex items-center gap-1">
              <Lock size={10} />
              {t("rpe.auto_close_admin_only")}
            </span>
          )}
        </div>
      </div>
      {error ? <p className="mt-1.5 text-[11px] text-red-500">{error}</p> : null}
    </div>
  );
};

export default RpeAutoCloseSettings;
